import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useGuardBypass } from '../hooks/useGuardBypass';
import { useLocation } from 'react-router-dom';
import { ROUTES_WITHOUT_ORG } from '@/core/constants/routes';
import { useOrganizations } from '@/shared/hooks/useOrganizations';
import type { Organization } from '@/core/types/organization.types';

export function OrganizationGuard() {
  const { t } = useTranslation('core');
  const bypass = useGuardBypass();
  const location = useLocation();
  const { orgId } = useParams<{ orgId: string }>();

  const setActiveOrganization = useOrganizationStore((s) => s.setActiveOrganization);
  const {
    data: organizations = [],
    isLoading: isLoadingOrgs,
    isError: isOrgsError,
    isSuccess: isOrgsSuccess,
    refetch: refetchOrgs,
  } = useOrganizations();

  const exists = !!orgId && organizations.some((o: Organization) => o.id === orgId);
  const defaultOrg = organizations[0];

  const isExemptRoute = ROUTES_WITHOUT_ORG.includes(
    location.pathname as (typeof ROUTES_WITHOUT_ORG)[number],
  );

  useEffect(() => {
    if (orgId && exists) {
      setActiveOrganization(orgId);
    }
  }, [orgId, exists, setActiveOrganization]);

  if (bypass || isExemptRoute) {
    return <Outlet />;
  }

  // Espera a que carguen las organizaciones antes de decidir. `isOrgsSuccess`
  // cubre además el hueco entre "aún no habilitada" y "ya en fetch" — sin él,
  // una query todavía pendiente-pero-no-fetching se leería como "sin datos"
  // igual que un array vacío real.
  if (isLoadingOrgs || (!isOrgsSuccess && !isOrgsError)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-muted-foreground text-sm">{t('loading')}</span>
      </div>
    );
  }

  // Falló la carga de organizaciones → nunca se sabe si el usuario tiene o no.
  // No redirigir a onboarding: eso solo debe pasar cuando la carga confirma
  // cero organizaciones, no cuando no pudimos confirmar nada.
  if (isOrgsError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">{t('errorBoundary.title')}</h1>
          <p className="text-sm text-muted-foreground mb-6">{t('errorBoundary.description')}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => refetchOrgs()}>
              {t('errorBoundary.retry')}
            </Button>
            <Button onClick={() => window.location.reload()}>{t('errorBoundary.reload')}</Button>
          </div>
        </div>
      </div>
    );
  }

  // Carga confirmada con cero organizaciones → el usuario no pertenece a ninguna
  if (!defaultOrg) {
    return <Navigate to="/onboarding" replace />;
  }

  // orgId inválido o no pertenece al usuario → redirige a la primera org
  if (!exists) {
    return <Navigate to={`/${defaultOrg.id}/members`} replace />;
  }

  return <Outlet />;
}
